"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Banner 1035 — BDU Programme Hero.
 *
 * Port of BDU v1 `components/Degree/Hero.tsx` + `components/organisms/Banner.tsx`.
 * Same shell as Banner 1025 (the home hero) with the two differences the programme
 * pages actually render:
 *
 *  1. the sub-heading is an `<h2>` for document outline, but styled as v1's small
 *     paragraph (`text-sm/[21px]` → `tab:text-lg/[27px]`) sitting under the title,
 *     NOT at the title's own type scale; and
 *  2. there are TWO CTAs — a transparent, white-bordered "Apply Now" and a
 *     gradient-filled "Brochure" with a download glyph.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as `tab:`.
 *
 * The reference's `md:ml-22` is NOT a real Tailwind class (there is no `22` in the
 * spacing scale) and emits no rule in the live production CSS, so the card's left
 * offset is `mx-5` (20px) at every width — the rendered behaviour, not the intent.
 *
 * TITLE LINE BREAKS — the CMS title is a textarea, and a newline typed into it is
 * honoured from `tab` (768px) up via `tab:whitespace-pre-line`. Below that the panel
 * is only 218px wide, so the mobile title keeps wrapping naturally and the authored
 * break is ignored.
 *
 * The v1 "Apply Now" button is shadcn's `outline` variant, but BDU's Tailwind config
 * declares `background: {}`, so `bg-background` emits NO rule: the live button is
 * transparent with a 1px white border. That is reproduced here as an explicit
 * transparent fill + CMS-driven border colour rather than copying the dead class.
 */

interface HeroSlide {
  id?: string | number;
  image?: string;
  alt_image?: string;
  /** Optional; falls back to `image` when empty */
  mobile_image?: string;
  mobile_alt_image?: string;
  title?: string;
  subtitle?: string;
  title_color?: string;
  subtitle_color?: string;
  priority?: number;
  status?: boolean;
}

interface AccLogo {
  id?: string | number;
  image?: string;
  alt_image?: string;
  priority?: number;
  status?: boolean;
}

interface Banner1035Data {
  /** Carousel slides */
  metadata?: HeroSlide[];
  /** Accreditation badges — section level, rendered on every slide that has a title */
  metadata_1?: AccLogo[];
  slide_frequency?: number;
  hero_section_height?: number;
  /** Overlay panel gradient (first slide, tab and up) */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Overlay panel fill below `tab` */
  background_color?: string;
  /** Brochure CTA gradient */
  gradient_color_3?: string;
  gradient_color_4?: string;
  /** Primary (outline) CTA */
  apply_now_show?: boolean;
  apply_now_text?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean;
  apply_now_color?: string;
  apply_now_border_color?: string;
  /** Brochure CTA */
  download_brochure_show?: boolean;
  download_brochure_text?: string;
  download_brochure_link?: string;
  download_brochure_is_cta?: boolean;
  cta_text_color?: string;
  /** Accreditation bar background */
  horizontal_stripe_color?: string;
  status?: boolean;
}

/** The 15x15 download glyph the reference pulls from its icon map. */
const DownloadIcon = ({ color }: { color: string }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M7.75022 11L3.13216 6.625L4.42522 5.35625L6.82661 7.63125V0.5H8.67383V7.63125L11.0752 5.35625L12.3683 6.625L7.75022 11ZM2.20855 14.5C1.70056 14.5 1.26585 14.3288 0.904411 13.9864C0.542971 13.644 0.361944 13.2318 0.361328 12.75V10.125H2.20855V12.75H13.2919V10.125H15.1391V12.75C15.1391 13.2312 14.9584 13.6434 14.5969 13.9864C14.2355 14.3294 13.8005 14.5 13.2919 14.5H2.20855Z"
      fill={color}
    />
  </svg>
);

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

function sortActive<T extends { priority?: number; status?: boolean }>(
  rows: T[] | undefined,
): T[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r) => r?.status !== false)
    .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0));
}

const Banner1035 = (props: { data?: Banner1035Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Banner1035Data | undefined;
  const dispatch = useDispatch();
  const [index, setIndex] = useState(0);

  const {
    slide_frequency = 5000,
    hero_section_height = 540,
    gradient_color_1 = "#13344F",
    gradient_color_2 = "#77B6EB",
    background_color = "#22405ADB",
    gradient_color_3 = "#FF0013",
    gradient_color_4 = "#990013",
    apply_now_text = "Apply Now",
    apply_now_link = "",
    apply_now_color = "#FFFFFF",
    apply_now_border_color = "#FFFFFF",
    download_brochure_text = "Brochure",
    download_brochure_link = "",
    cta_text_color = "#FFFFFF",
    horizontal_stripe_color = "#FFFFFF",
  } = sectionData || {};

  const applyNowShow = isCmsShowFlagOn(sectionData?.apply_now_show, true);
  const applyNowIsCta = isCmsShowFlagOn(sectionData?.apply_now_is_cta, true);
  const brochureShow = isCmsShowFlagOn(sectionData?.download_brochure_show, true);
  const brochureIsCta = isCmsShowFlagOn(
    sectionData?.download_brochure_is_cta,
    true,
  );
  const status = isCmsShowFlagOn(sectionData?.status, true);

  const slides = useMemo(
    () => sortActive(sectionData?.metadata),
    [sectionData?.metadata],
  );
  const accLogos = useMemo(
    () => sortActive(sectionData?.metadata_1).filter((a) => !!a?.image),
    [sectionData?.metadata_1],
  );

  const n = slides.length;

  // Autoplay — v1 advances on a fixed interval with no pause-on-hover.
  useEffect(() => {
    if (!status || n <= 1 || !slide_frequency) return undefined;
    const id = window.setInterval(
      () => setIndex((prev) => (prev === n - 1 ? 0 : prev + 1)),
      Number(slide_frequency),
    );
    return () => window.clearInterval(id);
  }, [status, n, slide_frequency]);

  // A slide count change (CMS edit / preview) can leave `index` past the end.
  useEffect(() => {
    if (n > 0 && index > n - 1) setIndex(0);
  }, [n, index]);

  const handleApplyNowClick = useCallback(() => {
    ctaClick({
      section: "banner_1035",
      component: "hero_apply_now",
      label: apply_now_text?.trim() || "apply_now",
      step: "1",
      entity: "banner_1035",
      parentEntity: "banner_1035",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [apply_now_text, dispatch]);

  const handleBrochureClick = useCallback(() => {
    ctaClick({
      section: "banner_1035",
      component: "hero_download_brochure",
      label: download_brochure_text?.trim() || "brochure",
      step: "1",
      entity: "banner_1035",
      parentEntity: "banner_1035",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: download_brochure_link || "",
      }),
    );
  }, [download_brochure_link, download_brochure_text, dispatch]);

  if (!status || n === 0) return null;

  const ctaBase = cn(
    "inline-flex h-auto items-center justify-center gap-2 whitespace-nowrap rounded-md",
    "px-6 py-2 text-base font-medium shadow transition-colors tab:text-lg",
  );
  const applyClassName = cn(ctaBase, "border tab:w-[170px]");
  const applyStyle = {
    // BDU's `bg-background` emits no rule (see the file header) — the live button
    // is transparent with a white border.
    backgroundColor: "transparent",
    borderColor: apply_now_border_color,
    color: apply_now_color,
  };
  const brochureClassName = cn(ctaBase, "tab:w-[200px]");
  const brochureStyle = {
    backgroundImage: `linear-gradient(to right, ${gradient_color_3}, ${gradient_color_4})`,
    color: cta_text_color,
  };

  return (
    <section
      className="relative h-[402px] w-full tab:h-[var(--bdu-hero-h)]"
      style={{ ["--bdu-hero-h" as string]: `${hero_section_height}px` }}
    >
      <div className="relative size-full overflow-hidden border-none">
        <div className="relative size-full">
          {slides.map((slide, i) => {
            const desktopSrc = slide.image || slide.mobile_image || "";
            const mobileSrc = slide.mobile_image || slide.image || "";
            const isFirst = i === 0;

            return (
              <div
                key={slide.id ?? i}
                className={cn(
                  "absolute inset-0 h-full w-full transition-transform duration-700 ease-in-out",
                  index === i ? "z-10 translate-x-0" : "z-0",
                  index < i && "translate-x-full",
                  index > i && "-translate-x-full",
                )}
              >
                {/* Desktop image */}
                <Image
                  src={desktopSrc}
                  alt={slide.alt_image || `Slide ${i + 1}`}
                  width={800}
                  height={540}
                  priority={isFirst}
                  loading={isFirst ? "eager" : "lazy"}
                  quality={85}
                  fetchPriority={isFirst ? "high" : "auto"}
                  sizes="100vw"
                  className="hidden size-full object-cover object-top tab:block"
                />
                {/* Mobile image */}
                <Image
                  src={mobileSrc}
                  alt={slide.mobile_alt_image || slide.alt_image || `Slide ${i + 1}`}
                  width={800}
                  height={402}
                  priority={isFirst}
                  loading={isFirst ? "eager" : "lazy"}
                  quality={85}
                  fetchPriority={isFirst ? "high" : "auto"}
                  sizes="100vw"
                  className="size-full object-cover object-top tab:hidden"
                />

                <div
                  className={cn(
                    "absolute inset-0 transition-opacity duration-700",
                    index === i ? "opacity-100" : "opacity-0",
                  )}
                >
                  {slide.title ? (
                    <div
                      className="absolute inset-0 flex size-full w-full rounded-none tab:w-[560px]"
                      // Only the first slide carries the panel fill in the reference.
                      style={
                        isFirst
                          ? { backgroundColor: background_color }
                          : undefined
                      }
                    >
                      {isFirst && (
                        <div
                          aria-hidden
                          className="absolute inset-0 hidden size-full tab:block"
                          style={{
                            backgroundImage: `linear-gradient(90deg, ${gradient_color_1}, ${gradient_color_2})`,
                          }}
                        />
                      )}

                      <div
                        className={cn(
                          "relative flex w-full items-center p-0",
                          "mx-5 mb-8 mt-[72px] h-[298px]",
                          "tab:mb-14 tab:mt-[88px] tab:h-[398px] tab:w-[500px]",
                        )}
                      >
                        <div className="flex h-full flex-col items-start justify-between text-left text-white">
                          {isFirst ? (
                            <h1
                              className="w-[218px] text-2xl font-bold tab:w-[454px] tab:whitespace-pre-line tab:text-[42px]/[50px]"
                              style={{ color: slide.title_color || "#FFFFFF" }}
                            >
                              {slide.title}
                            </h1>
                          ) : (
                            <p
                              className="w-[218px] text-2xl font-bold tab:w-[454px] tab:whitespace-pre-line tab:text-[42px]/[50px]"
                              style={{ color: slide.title_color || "#FFFFFF" }}
                            >
                              {slide.title}
                            </p>
                          )}

                          {/*
                            Sub-heading sits BELOW the title at a much smaller scale —
                            v1 renders it as `text-sm/[21px] … md:text-lg/[27px]`, not at
                            the title's own size. Kept as an h2 for document outline, but
                            styled as the reference's paragraph.
                          */}
                          {slide.subtitle && (
                            <h2
                              className="mt-1.5 w-[201px] flex-wrap text-sm/[21px] font-normal tab:w-[468px] tab:text-lg/[27px] tab:leading-7"
                              style={{
                                color: slide.subtitle_color || "#FFFFFF",
                              }}
                            >
                              {slide.subtitle}
                            </h2>
                          )}

                          {(applyNowShow || brochureShow) && (
                            <div className="mt-[18px] flex flex-col gap-2 tab:mt-8 tab:flex-row">
                              {applyNowShow &&
                                apply_now_text &&
                                (applyNowIsCta || !apply_now_link ? (
                                  <button
                                    type="button"
                                    onClick={handleApplyNowClick}
                                    className={applyClassName}
                                    style={applyStyle}
                                  >
                                    {apply_now_text}
                                  </button>
                                ) : (
                                  <Link
                                    href={normalizeHref(apply_now_link)}
                                    className={applyClassName}
                                    style={applyStyle}
                                  >
                                    {apply_now_text}
                                  </Link>
                                ))}

                              {/* v1 hides the brochure button when the programme has no brochure. */}
                              {brochureShow &&
                                download_brochure_text &&
                                (brochureIsCta || !download_brochure_link ? (
                                  <button
                                    type="button"
                                    onClick={handleBrochureClick}
                                    className={brochureClassName}
                                    style={brochureStyle}
                                  >
                                    <DownloadIcon color={cta_text_color} />
                                    <span>{download_brochure_text}</span>
                                  </button>
                                ) : (
                                  <Link
                                    href={normalizeHref(download_brochure_link)}
                                    className={brochureClassName}
                                    style={brochureStyle}
                                  >
                                    <DownloadIcon color={cta_text_color} />
                                    <span>{download_brochure_text}</span>
                                  </Link>
                                ))}
                            </div>
                          )}

                          {accLogos.length > 0 && (
                            <div
                              className={cn(
                                "mt-auto flex h-10 w-auto flex-row items-center justify-center rounded",
                                "tab:h-[62px] tab:justify-start tab:rounded-md",
                              )}
                              style={{
                                backgroundColor: horizontal_stripe_color,
                              }}
                            >
                              {accLogos.map((logo, li) => (
                                <div
                                  key={logo.id ?? li}
                                  className="flex h-full items-center justify-center p-1.5 tab:max-w-[125px] tab:p-2.5"
                                >
                                  <Image
                                    src={logo.image || ""}
                                    alt={logo.alt_image || ""}
                                    width={60}
                                    height={24}
                                    className="h-7 w-full tab:h-[50px]"
                                    priority
                                    loading="eager"
                                    quality={85}
                                    fetchPriority="high"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Banner1035;
